import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Users, Plus, Trash2, ShieldCheck, Crown, Loader2, Save, UserPlus, Power, Camera } from "lucide-react";

const PERM_LABEL = {
  orders: "Commandes", tickets: "Tickets", solutions: "Bibliothèque",
  users: "Clients", payments: "Paiements", staff: "Staff", audit: "Audit",
};
const initials = (n) => (n || "?").trim()[0]?.toUpperCase() || "?";

const StaffCard = ({ card, permKeys, onChanged }) => {
  const [name, setName] = useState(card.name);
  const [title, setTitle] = useState(card.title || "");
  const [perms, setPerms] = useState(card.permissions);
  const [active, setActive] = useState(card.active);
  const [busy, setBusy] = useState(false);
  const [pic, setPic] = useState(card.picture);
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInput = useRef(null);
  const owner = card.is_owner;

  const togglePerm = (k) => !owner && setPerms((p) => ({ ...p, [k]: !p[k] }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Choisissez une image"); return; }
    setPhotoBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/staff/${encodeURIComponent(card.email)}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPic(data.picture);
      toast.success("Photo mise à jour");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'envoi"); }
    setPhotoBusy(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/staff/${encodeURIComponent(card.email)}`, { name, title, permissions: perms, active });
      toast.success("Staff mis à jour");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };

  const toggleActive = async () => {
    const next = !active; setActive(next); setBusy(true);
    try {
      await api.put(`/admin/staff/${encodeURIComponent(card.email)}`, { active: next });
      toast.success(next ? "Compte activé" : "Compte désactivé");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setActive(!next); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm(`Retirer ${card.email} du staff ? Son accès admin sera révoqué.`)) return;
    setBusy(true);
    try { await api.delete(`/admin/staff/${encodeURIComponent(card.email)}`); toast.success("Staff retiré"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setBusy(false); }
  };

  return (
    <div data-testid={`staff-card-${card.email}`} className={`rounded-md border p-5 ${owner ? "border-aurora-purple/30 bg-aurora-purple/5" : active ? "border-white/10 bg-black/20" : "border-white/5 bg-black/10 opacity-70"}`}>
      <div className="flex items-start gap-3 mb-4">
        <button type="button" onClick={() => photoInput.current?.click()} disabled={photoBusy}
          data-testid={`staff-avatar-upload-${card.email}`} title="Changer la photo"
          className="relative w-11 h-11 rounded-full overflow-hidden shrink-0 group">
          {pic ? <img src={pic} alt="" className="w-11 h-11 rounded-full object-cover" /> :
            <div className="w-11 h-11 rounded-full bg-aurora-cyan/15 grid place-items-center text-aurora-cyan font-bold">{initials(card.name)}</div>}
          <span className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 grid place-items-center transition-opacity">
            {photoBusy ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Camera className="w-4 h-4 text-white" />}
          </span>
        </button>
        <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadPhoto(e.target.files[0]); e.target.value = ""; }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {owner ? (
              <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm border border-aurora-purple/50 text-aurora-purple flex items-center gap-1"><Crown className="w-2.5 h-2.5" /> Propriétaire</span>
            ) : (
              <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${active ? "border-signal-success/40 text-signal-success" : "border-white/15 text-zinc-500"}`}>{active ? "Actif" : "Inactif"}</span>
            )}
          </div>
          <span className="font-mono text-[8px] uppercase tracking-widest text-zinc-500 block mb-1">Nom affiché aux clients</span>
          <input data-testid={`staff-name-${card.email}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Yvan"
            className="w-full bg-white/5 border border-aurora-cyan/30 rounded-sm px-2.5 py-1.5 focus:border-aurora-cyan outline-none font-display uppercase tracking-tight text-sm" />
          <div className="font-mono text-[11px] text-aurora-cyan truncate mt-1.5">{card.email}</div>
          <span className="font-mono text-[8px] uppercase tracking-widest text-zinc-500 block mt-2 mb-1">Intitulé / rôle (optionnel)</span>
          <input data-testid={`staff-title-${card.email}`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex. Ingénieur calibration"
            className="w-full bg-white/5 border border-white/15 rounded-sm px-2.5 py-1.5 focus:border-aurora-cyan outline-none font-mono text-[11px] text-zinc-300" />
        </div>
      </div>

      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-2">Autorisations</div>
      <div className="flex flex-wrap gap-2 mb-4">
        {permKeys.map((k) => {
          const on = owner || perms[k];
          return (
            <button key={k} data-testid={`perm-${card.email}-${k}`} onClick={() => togglePerm(k)} disabled={owner}
              className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm border transition-colors ${on ? "border-aurora-cyan/50 text-aurora-cyan bg-aurora-cyan/10" : "border-white/10 text-zinc-500 hover:border-white/30"} ${owner ? "cursor-default" : ""}`}>
              {PERM_LABEL[k] || k}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <button data-testid={`staff-save-${card.email}`} onClick={save} disabled={busy}
          className="flex items-center gap-1.5 px-4 py-2 bg-aurora-cyan text-white font-display font-bold uppercase tracking-wider text-xs rounded-sm disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
        </button>
        {!owner && (
          <>
            <button onClick={toggleActive} disabled={busy} title={active ? "Désactiver" : "Activer"}
              className={`w-9 h-9 grid place-items-center rounded-sm border transition-colors ${active ? "border-white/15 text-zinc-400 hover:border-signal-processing hover:text-signal-processing" : "border-signal-success/40 text-signal-success"}`}>
              <Power className="w-4 h-4" />
            </button>
            <button data-testid={`staff-remove-${card.email}`} onClick={remove} disabled={busy} title="Retirer"
              className="w-9 h-9 grid place-items-center rounded-sm border border-white/10 text-zinc-500 hover:border-signal-error hover:text-signal-error transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const StaffManager = () => {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/admin/staff").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Email valide requis"); return; }
    setAdding(true);
    try {
      await api.post("/admin/staff", { email, name, permissions: { orders: true, tickets: true } });
      toast.success("Membre ajouté au staff");
      setEmail(""); setName(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setAdding(false);
  };

  if (!data) return null;

  return (
    <div className="glass rounded-md p-6 mt-5" data-testid="staff-manager">
      <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-1 flex items-center gap-2">
        <Users className="w-5 h-5 text-aurora-cyan" /> Gestion du staff
      </h3>
      <p className="font-mono text-[10px] text-zinc-500 mb-5 flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" /> Gérez vos administrateurs et leurs autorisations. Les comptes propriétaires ont tous les droits.
      </p>

      {/* Add staff */}
      <div className="flex flex-col sm:flex-row gap-2 mb-6 p-4 rounded-sm border border-dashed border-white/15">
        <input data-testid="new-staff-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@admin.com (compte Google)"
          className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:border-aurora-cyan outline-none" />
        <input data-testid="new-staff-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom (optionnel)"
          className="sm:w-48 bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:border-aurora-cyan outline-none" />
        <button data-testid="add-staff-btn" onClick={add} disabled={adding}
          className="shrink-0 flex items-center justify-center gap-2 px-5 py-2.5 bg-aurora-cyan text-white font-display font-bold uppercase tracking-wider text-xs rounded-sm disabled:opacity-50">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Ajouter
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {data.staff.map((c) => (
          <StaffCard key={c.email} card={c} permKeys={data.permission_keys} onChanged={load} />
        ))}
      </div>
    </div>
  );
};
